import React, { useEffect, useState } from "react";
import "./Security.css";
import useRevealOnIntersect from "../hooks/useRevealOnIntersect";
import { useScrollToTopOnMount } from "../hooks/useScrollToTop";
import heroImg from "../assets/heroo.jpg";
import overview1Img from "../assets/overview1.jpg";
import overview2Img from "../assets/overview2.jpg";
import service1 from "../assets/service1.jpg";
import service2 from "../assets/service2.png";
import service3 from "../assets/service3.png";
import service4 from "../assets/service4.png";
import service5 from "../assets/service5.png";
import service6 from "../assets/service6.png";
import service7 from "../assets/service7.png";
import service8 from "../assets/service8.png";

/* Static section IDs moved outside component to avoid hook dependency warning */
const SECTION_IDS = ["overview", "portfolio", "gallery", "why", "contact"];

export default function Security() {
  useScrollToTopOnMount();
  useRevealOnIntersect(".sec-reveal", { threshold: 0.18 });

  /* Active quick nav highlight */
  const [active, setActive] = useState("overview");
  useEffect(() => {
    if (!("IntersectionObserver" in window)) return;
    const observers = [];
    SECTION_IDS.forEach((id) => {
      const el = document.getElementById(id);
      if (!el) return;
      const obs = new IntersectionObserver(
        (entries) => {
          entries.forEach((en) => {
            if (en.isIntersecting) setActive(id);
          });
        },
        { rootMargin: "-40% 0px -48% 0px", threshold: 0.1 }
      );
      obs.observe(el);
      observers.push(obs);
    });
    return () => observers.forEach((o) => o.disconnect());
  }, []); // SECTION_IDS now stable at module scope

  const portfolio = [
    "CCTV Surveillance & Safe City Solutions – 24/7 monitoring with high-definition imaging and AI-based video analytics.",
    "Fire Suppression Systems – Intelligent detection and rapid-response suppression technologies.",
    "Forensic & Investigation Tools – Reliable equipment for digital and physical evidence collection.",
    "Thermal Scanners & Access Monitoring – Automated temperature and identity verification systems.",
    "Signal & Frequency Control – Jammers, vehicle-mounted systems, and frequency detectors for controlled environments.",
    "Anti-Surveillance & Eavesdropping Solutions – Counterintelligence tools to secure sensitive communication.",
    "Physical Barrier Systems – Hydraulic bomb barriers, UVIS / UVSS systems for secure vehicle inspection.",
    "Artificial Intelligence – Analytics elevating situational awareness and threat prioritization.",
    "Vehicle Mounted Jammers – Mobile signal suppression for mission-critical convoys.",
    "Frequency Detectors – Identify, log, and classify active transmissions.",
    "EVES Dropping Machine Countermeasures – Protect sensitive rooms and meetings.",
    "Thermal + Multi-Spectral Monitoring – Enhanced perimeter & elevated temperature screening."
  ];

  const reasons = [
    "AI-Powered Analytics",
    "End-to-End Integration",
    "Mission-Critical Reliability",
    "Scalable for Enterprises, Campuses & Cities"
  ];

  const gallery = [
    { id: 1, src: service1, alt: "Secure operations center dashboard live metrics" },
    { id: 2, src: service2, alt: "High‑definition CCTV camera deployment on pole mount" },
    { id: 3, src: service3, alt: "Thermal imaging / multi‑spectral perimeter scanner" },
    { id: 4, src: service4, alt: "Access control panel with multi‑factor interface" },
    { id: 5, src: service5, alt: "Vehicle mounted jammer equipment installation" },
    { id: 6, src: service6, alt: "Frequency detection hardware array in rack" },
    { id: 7, src: service7, alt: "Eavesdropping countermeasure sweep device" },
    { id: 8, src: service8, alt: "Hydraulic barrier / UVIS inspection system" }
  ];

  // Classify gallery images for optimal fit without losing important content
  useEffect(() => {
    function classify(img) {
      const w = img.naturalWidth;
      const h = img.naturalHeight;
      const cell = img.closest('.gallery-cell');
      if (!cell) return;
      if (h > w * 1.15) cell.classList.add('portrait');
      else if (w > h * 1.3) cell.classList.add('landscape');
      else cell.classList.add('square');
    }
    const imgs = Array.from(document.querySelectorAll('.sec-gallery img'));
    imgs.forEach(img => {
      if (img.complete) classify(img);
      else img.addEventListener('load', () => classify(img), { once: true });
    });
  }, [gallery.length]);

  return (
    <main className="security-page">
      <header className="sec-hero">
        <div className="sec-hero-bg" aria-hidden="true">
          <span className="blob b1" />
          <span className="blob b2" />
          <span className="blob b3" />
          <span className="grid-glow" />
          <span className="hero-vignette" />
        </div>
        <div className="sec-container hero-layout">
          <div className="hero-copy sec-reveal" data-reveal="up" style={{ "--d": "40ms" }}>
            <h1>
              Smart, <span className="gradient-word">Scalable</span>, and Secure
            </h1>
            <p className="subtitle">
              Advanced surveillance, AI-powered threat detection, and smart safety systems for a secure digital & physical environment.
            </p>
            <p className="hero-lead">
              At <strong>NEXITE</strong>, we deliver integrated security technologies that protect people, property, and information.
              AI‑driven analytics, intelligent surveillance, and proactive detections combine to provide total situational awareness & rapid response.
            </p>
            <div className="hero-actions">
              <a href="#overview" className="sec-btn sec-btn-primary">
                <span className="shine" aria-hidden="true" />
                Explore Overview
              </a>
              <a href="#contact" className="sec-btn sec-btn-secondary">
                Request a Security Audit
              </a>
            </div>
            <ul className="hero-stats">
              <li>
                <div className="num">24/7</div>
                <div className="lab">Monitoring</div>
              </li>
              <li>
                <div className="num">AI</div>
                <div className="lab">Analytics</div>
              </li>
              <li>
                <div className="num">End‑to‑End</div>
                <div className="lab">Integration</div>
              </li>
            </ul>
          </div>

          <div className="hero-media sec-reveal" data-reveal="up" style={{ "--d": "120ms" }}>
            <div className="media-edge" aria-hidden="true" />
            <div className="media-overlay" aria-hidden="true" />
            <img
              src={heroImg}
              alt="Field deployment vehicle with secure comms"
              loading="eager"
              onError={(e) => (e.currentTarget.style.visibility = "hidden")}
            />
          </div>
        </div>

        <nav className="quicknav-wrap">
          <div
            className="sec-quicknav sec-reveal"
            data-reveal="up"
            style={{ "--d": "180ms" }}
            aria-label="Page sections"
          >
            {SECTION_IDS.map((id) => (
              <a key={id} href={`#${id}`} className={active === id ? "active" : ""}>
                {id === "why" ? "Why NEXITE" : id.charAt(0).toUpperCase() + id.slice(1)}
              </a>
            ))}
            <span className="underline" aria-hidden="true" />
          </div>
        </nav>
        <div className="sec-hero-accent" aria-hidden="true" />
      </header>

      <section id="overview" className="sec-section">
        <div className="sec-container split">
          <div className="split-col sec-reveal" data-reveal="left">
            <h2>Overview</h2>
            <p className="lead">
              Layered defense across vision, access, signal & data—unified for centralized command, accelerated decisions, and resilient continuity.
            </p>
            <ul className="bullets">
              <li>Fire Suppression</li>
              <li>CCTV Surveillance / Safe City Solutions</li>
              <li>Forensic Tools & Investigation</li>
              <li>Thermal Scanners & Multi‑Spectral Monitoring</li>
              <li>Jammers & Signal Management</li>
              <li>Vehicle Mounted Jammers</li>
              <li>Frequency Detectors</li>
              <li>Eavesdropping Countermeasures</li>
              <li>Hydraulic Bomb Barriers</li>
              <li>UVIS / UVSS Inspection Systems</li>
              <li>Artificial Intelligence Analytics</li>
            </ul>
          </div>
          <div className="split-col media-stack sec-reveal" data-reveal="right">
            <figure className="media-card">
              <img
                src={overview1Img}
                alt="Surveillance analytics interface"
                loading="lazy"
                onError={(e) => (e.currentTarget.style.visibility = "hidden")}
              />
              <figcaption>High‑definition CCTV + AI</figcaption>
            </figure>
            <figure className="media-card">
              <img
                src={overview2Img}
                alt="Secure access control panel"
                loading="lazy"
                onError={(e) => (e.currentTarget.style.visibility = "hidden")}
              />
              <figcaption>Access & perimeter control</figcaption>
            </figure>
          </div>
        </div>
      </section>

      <section id="portfolio" className="sec-section alt">
        <div className="sec-container">
          <h2 className="sec-reveal" data-reveal="up">Security Portfolio</h2>
          <div className="portfolio-list sec-reveal" data-reveal="up" style={{ "--d": "80ms" }}>
            {portfolio.map((item, i) => (
              <div key={i} className="portfolio-item">
                <span className="pi-dot" aria-hidden="true" />
                <p>{item}</p>
                <span className="edge-glow" aria-hidden="true" />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="gallery" className="sec-section">
        <div className="sec-container">
          <h2 className="sec-reveal" data-reveal="up">Visual Gallery</h2>
          <p className="lead sec-reveal" data-reveal="up" style={{ "--d": "60ms" }}>
            Demonstration slots for deployment, hardware, monitoring, analytics & command scenes.
          </p>
          <div className="sec-gallery sec-reveal" data-reveal="up" style={{ "--d": "120ms" }}>
            {gallery.map((g, idx) => (
              <figure key={g.id} className="gallery-cell" style={{ "--d": `${idx * 70}ms` }}>
                <img
                  src={g.src}
                  alt={g.alt}
                  loading="lazy"
                  decoding="async"
                  onError={(e) => (e.currentTarget.style.visibility = "hidden")}
                />
                <figcaption>{g.alt}</figcaption>
                <span className="edge-glow" aria-hidden="true" />
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section id="why" className="sec-section alt">
        <div className="sec-container">
          <h2 className="sec-reveal" data-reveal="up">Why NEXITE Security?</h2>
          <div className="why-grid sec-reveal" data-reveal="up" style={{ "--d": "80ms" }}>
            {reasons.map((r) => (
              <div key={r} className="why-card">
                <div className="why-icon">✔</div>
                <h3>{r}</h3>
                <span className="edge-glow" aria-hidden="true" />
              </div>
            ))}
          </div>
          <p className="mission sec-reveal" data-reveal="up" style={{ "--d": "160ms" }}>
            Protecting what matters — with intelligence, innovation & integrity.
          </p>
        </div>
      </section>

      <section id="contact" className="sec-section cta">
        <div className="sec-container sec-reveal" data-reveal="up">
          <h2>Talk to a Security Specialist</h2>
          <p className="lead">
            Send site profile & objectives — receive a right‑sized, actionable proposal.
          </p>
          <a href="/contact" className="sec-btn sec-btn-primary">
            <span className="shine" aria-hidden="true" />
            Contact Us
          </a>
        </div>
      </section>
    </main>
  );
}